export type AccessCategory = 'public' | 'authenticated' | 'admin';

export interface RouteMeta {
  readonly id: string;
  readonly path: string;
  readonly title: string;
  readonly description: string;
  readonly accessCategory: AccessCategory;
  readonly showInNav: boolean;
  readonly navSection?: 'main' | 'mobile' | 'admin';
  readonly navLabel?: string;
}

export const ROUTES: Record<string, RouteMeta> = {
  // Public Routes
  HOME: {
    id: 'home',
    path: '/',
    title: 'Home',
    description: 'PAY888 production-oriented fintech web platform foundation.',
    accessCategory: 'public',
    showInNav: true,
    navSection: 'main',
    navLabel: 'Home',
  },
  LOGIN: {
    id: 'login',
    path: '/login',
    title: 'Sign In',
    description: 'Sign in to your PAY888 account.',
    accessCategory: 'public',
    showInNav: false,
  },
  REGISTER: {
    id: 'register',
    path: '/register',
    title: 'Create Account',
    description: 'Register a new PAY888 account.',
    accessCategory: 'public',
    showInNav: false,
  },
  FORGOT_PASSWORD: {
    id: 'forgotPassword',
    path: '/forgot-password',
    title: 'Reset Password',
    description: 'Request a password reset link.',
    accessCategory: 'public',
    showInNav: false,
  },
  VERIFY_EMAIL: {
    id: 'verifyEmail',
    path: '/verify-email',
    title: 'Verify Email',
    description: 'Verify your email address to activate your account.',
    accessCategory: 'public',
    showInNav: false,
  },
  PUBLIC_PAY: {
    id: 'publicPay',
    path: '/pay/:slug',
    title: 'Public Payment Link',
    description: 'PAY888 public payment link interface.',
    accessCategory: 'public',
    showInNav: false,
  },

  // Application Routes (protected by Firebase Authentication)
  DASHBOARD: {
    id: 'dashboard',
    path: '/dashboard',
    title: 'Dashboard',
    description: 'Overview of your account status and recent activity.',
    accessCategory: 'authenticated',
    showInNav: true,
    navSection: 'main',
    navLabel: 'Dashboard',
  },
  RECEIVE: {
    id: 'receive',
    path: '/receive',
    title: 'Receive Money',
    description: 'Payment links, QR codes, and acquiring overview.',
    accessCategory: 'authenticated',
    showInNav: true,
    navSection: 'main',
    navLabel: 'Receive',
  },
  RECEIVE_CREATE: {
    id: 'receiveCreate',
    path: '/receive/create',
    title: 'Create Payment Link',
    description: 'Generate a new payment link for receiving funds.',
    accessCategory: 'authenticated',
    showInNav: false,
  },
  RECEIVE_PAYMENTS: {
    id: 'receivePayments',
    path: '/receive/payments',
    title: 'Incoming Payments',
    description: 'History of received payments and settlements.',
    accessCategory: 'authenticated',
    showInNav: false,
  },
  RECEIVE_LINKS: {
    id: 'receiveLinks',
    path: '/receive/links',
    title: 'Active Payment Links',
    description: 'Manage your active payment links.',
    accessCategory: 'authenticated',
    showInNav: false,
  },
  CARDS: {
    id: 'cards',
    path: '/cards',
    title: 'Virtual Cards',
    description: 'Manage your virtual cards and card spending limits.',
    accessCategory: 'authenticated',
    showInNav: true,
    navSection: 'main',
    navLabel: 'Cards',
  },
  CARDS_CREATE: {
    id: 'cardsCreate',
    path: '/cards/create',
    title: 'Issue Virtual Card',
    description: 'Request a new virtual card.',
    accessCategory: 'authenticated',
    showInNav: false,
  },
  CARDS_TRANSACTIONS: {
    id: 'cardsTransactions',
    path: '/cards/transactions',
    title: 'Card Transactions',
    description: 'Virtual card transaction history.',
    accessCategory: 'authenticated',
    showInNav: false,
  },
  ACTIVITY: {
    id: 'activity',
    path: '/activity',
    title: 'Activity Log',
    description: 'Complete transaction and activity history.',
    accessCategory: 'authenticated',
    showInNav: true,
    navSection: 'main',
    navLabel: 'Activity',
  },
  SERVICES: {
    id: 'services',
    path: '/services',
    title: 'Services',
    description: 'Explore eSIM, gift cards, mobile top-ups and other planned services.',
    accessCategory: 'authenticated',
    showInNav: true,
    navSection: 'main',
    navLabel: 'Services',
  },
  REFERRALS: {
    id: 'referrals',
    path: '/referrals',
    title: 'Invite Friends',
    description: 'Share your personal PAY888 invitation link.',
    accessCategory: 'authenticated',
    showInNav: true,
    navSection: 'main',
    navLabel: 'Invite friends',
  },
  SETTINGS: {
    id: 'settings',
    path: '/settings',
    title: 'Settings',
    description: 'Account settings, security options, and preferences.',
    accessCategory: 'authenticated',
    showInNav: true,
    navSection: 'main',
    navLabel: 'Settings',
  },
  SUPPORT: {
    id: 'support',
    path: '/support',
    title: 'Support & Help',
    description: 'Get help or contact PAY888 support.',
    accessCategory: 'authenticated',
    showInNav: true,
    navSection: 'main',
    navLabel: 'Support',
  },

  // Admin Routes (protected by a server-issued Firebase admin custom claim)
  ADMIN: {
    id: 'admin',
    path: '/admin',
    title: 'Admin Console',
    description: 'Platform management and administrative controls.',
    accessCategory: 'admin',
    showInNav: true,
    navSection: 'admin',
    navLabel: 'Overview',
  },
  ADMIN_USERS: {
    id: 'adminUsers',
    path: '/admin/users',
    title: 'Admin User Management',
    description: 'User accounts, status, and role inspection.',
    accessCategory: 'admin',
    showInNav: true,
    navSection: 'admin',
    navLabel: 'Users',
  },
  ADMIN_PAYMENTS: {
    id: 'adminPayments',
    path: '/admin/payments',
    title: 'Admin Payment Audit',
    description: 'System-wide payment and settlement logs.',
    accessCategory: 'admin',
    showInNav: true,
    navSection: 'admin',
    navLabel: 'Payments',
  },
  ADMIN_CARDS: {
    id: 'adminCards',
    path: '/admin/cards',
    title: 'Admin Card Operations',
    description: 'Virtual card issuance monitoring.',
    accessCategory: 'admin',
    showInNav: true,
    navSection: 'admin',
    navLabel: 'Cards',
  },
  ADMIN_REVENUE: {
    id: 'adminRevenue',
    path: '/admin/revenue',
    title: 'Admin Revenue Analytics',
    description: 'Platform fee collection and analytics.',
    accessCategory: 'admin',
    showInNav: true,
    navSection: 'admin',
    navLabel: 'Revenue',
  },
  ADMIN_PROVIDERS: {
    id: 'adminProviders',
    path: '/admin/providers',
    title: 'Admin Provider Health',
    description: 'Financial provider status and webhook health.',
    accessCategory: 'admin',
    showInNav: true,
    navSection: 'admin',
    navLabel: 'Providers',
  },
  ADMIN_SETTINGS: {
    id: 'adminSettings',
    path: '/admin/settings',
    title: 'Admin System Settings',
    description: 'Global system flags and rate limit configurations.',
    accessCategory: 'admin',
    showInNav: true,
    navSection: 'admin',
    navLabel: 'Settings',
  },
  ADMIN_AUDIT: {
    id: 'adminAudit',
    path: '/admin/audit',
    title: 'Admin Audit Logs',
    description: 'System audit trail and security event logs.',
    accessCategory: 'admin',
    showInNav: true,
    navSection: 'admin',
    navLabel: 'Audit Logs',
  },
};

export const MAIN_NAV_ITEMS: RouteMeta[] = [
  ROUTES.DASHBOARD,
  ROUTES.CARDS,
  ROUTES.SERVICES,
  ROUTES.ACTIVITY,
  ROUTES.SETTINGS,
  ROUTES.REFERRALS,
  ROUTES.SUPPORT,
];

export const MOBILE_BOTTOM_NAV_ITEMS: RouteMeta[] = [
  ROUTES.DASHBOARD,
  ROUTES.CARDS,
  ROUTES.SERVICES,
  ROUTES.ACTIVITY,
];

export const ADMIN_NAV_ITEMS: RouteMeta[] = [
  ROUTES.ADMIN,
  ROUTES.ADMIN_USERS,
  ROUTES.ADMIN_PAYMENTS,
  ROUTES.ADMIN_CARDS,
  ROUTES.ADMIN_REVENUE,
  ROUTES.ADMIN_PROVIDERS,
  ROUTES.ADMIN_SETTINGS,
  ROUTES.ADMIN_AUDIT,
];

export function updateDocumentTitle(title: string): void {
  document.title = `PAY888 — ${title}`;
}
